import React from "react";
import { Box } from "@mui/material";
import { color, space } from "../../design/tokens";

/**
 * A full-width band of the page, with the standard vertical rhythm and the
 * grid container inside it.
 *
 * `tone` picks the ground:
 *   paper  — the default page ivory
 *   deep   — warm stone, for alternating bands
 *   ink    — full-bleed near-black, used sparingly (hero, featured project,
 *            closing CTA, footer) so each dark section still lands
 *
 * `bleed` drops the inner container for sections that run edge to edge.
 */
const TONES = {
  paper: { backgroundColor: color.paper, color: color.ink },
  deep: { backgroundColor: color.paperDeep, color: color.ink },
  ink: { backgroundColor: color.ink, color: color.onInk },
  none: {},
};

const Section = ({
  children,
  tone = "paper",
  size = "default",
  bleed = false,
  as = "section",
  topRule = false,
  id,
  sx,
  innerSx,
  ...rest
}) => {
  const paddingY =
    size === "tight"
      ? space.sectionYTight
      : size === "flush"
        ? 0
        : space.sectionY;

  return (
    <Box
      component={as}
      id={id}
      sx={{
        ...TONES[tone],
        paddingBlock: paddingY,
        position: "relative",
        // Every band is independently clipped so one wide child can never
        // give the whole document a horizontal scrollbar.
        overflowX: "clip",
        ...(topRule
          ? { borderTop: `1px solid ${tone === "ink" ? color.lineOnInk : color.line}` }
          : {}),
        ...sx,
      }}
      {...rest}
    >
      {bleed ? (
        children
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: space.maxWidth,
            marginInline: "auto",
            paddingInline: space.gutter,
            ...innerSx,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

/**
 * The grid container on its own, for use inside a `bleed` section where only
 * part of the content should be constrained.
 */
export const Inner = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: space.maxWidth,
      marginInline: "auto",
      paddingInline: space.gutter,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

export default Section;
